import {
  AppPageHeader,
  ColorSwatch,
  ColumnsType,
  Group,
  InfiniteTable,
} from '@/components/_common';
import ActionIcon from '@/components/_common/ActionIcon';
import BankConciliationPopoverDetails from '@/components/_common/BankConciliation/BankConciliationPopoverDetails';
import StatusTag from '@/components/_common/StatusTag';
import InstallmentsAdvancedModalFilters from '@/components/Registry/BillToPay/ReleaseForm/InstallmentsAdvancedModalFilters';
import InstallmentsExtraFilters from '@/components/Registry/BillToPay/ReleaseForm/InstallmentsExtraFilters';
import { useInstallmentsList } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import {
  InstallmentList,
  AccountPayableFieldOption,
  InstallmentSituation,
  InstallmentSituationType,
  AccountPayableSituationColor,
  ListInstallmentsQuery,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { useFinancialConsultationPayableBind } from '@/core/domain/financial/Consultation/consultation.hooks';
import { useDebounce, useDisclosure } from '@/hooks';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Popover, Modal, Button, Tooltip } from 'antd';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { Check, Link } from 'react-feather';
import { useIntl } from 'react-intl';

interface Props {
  migrationId: number;
}

export default function BillToPayMigrationConsultBind(props: Props) {
  const intl = useIntl();

  const [opened, action] = useDisclosure();

  const [queryParams, setQueryParams] = useState<ListInstallmentsQuery>({
    order: 'DESC',
  });

  const queryDebounce = useDebounce(queryParams, 500);
  const installmentList = useInstallmentsList(queryDebounce, opened);
  const consultPayableBind = useFinancialConsultationPayableBind();

  const [advancedFiltersOpened, advancedFiltersAction] = useDisclosure();

  const isLoading = consultPayableBind.isLoading;

  const installments = flatInfiniteData(installmentList.data);

  const handleSubmit = async (installment: InstallmentList) => {
    try {
      await consultPayableBind.mutateAsync({
        consultationId: props.migrationId,
        mainInstallmentPayableId: installment.id,
        mainPayableId: installment.accountPayableId,
      });
      action.close();
    } catch {}
  };

  const handleClose = useCallback(() => {
    if (isLoading) return;
    action.close();
  }, [isLoading]);

  const columns: ColumnsType<InstallmentList> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'releaseBillToPay.list.header.code' }),
      width: 140,
      key: AccountPayableFieldOption.code,
      sorter: true,
      render: (_, row) => (
        <Group gap={10}>
          <>{row.code}</>

          {!!row.conciliateInstallmentResponse && (
            <BankConciliationPopoverDetails
              conciliatedInstallment={row.conciliateInstallmentResponse}
            />
          )}
        </Group>
      ),
    },
    {
      dataIndex: 'installmentTag',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.installmentTag',
      }),
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.installmentTag,
      align: 'center',
      render: (_, row) =>
        !!row.installmentTag && (
          <Group position="center">
            <Tooltip
              title={row.installmentTag.name.toUpperCase()}
              color={row.installmentTag.color}
              placement="right"
            >
              <div>
                <ColorSwatch backgroundColor={row.installmentTag.color} />
              </div>
            </Tooltip>
          </Group>
        ),
    },
    {
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.companies',
      }),
      width: 220,
      key: AccountPayableFieldOption.company,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'documentNumber',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentNumber',
      }),
      width: 220,
      key: AccountPayableFieldOption.documentNumber,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'creditor',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.creditor',
      }),
      width: 220,
      key: AccountPayableFieldOption.creditor,
      sorter: true,
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.description',
      }),
      width: COLUMNS_WIDTH.action2,
      key: AccountPayableFieldOption.description,
      sorter: true,
    },
    {
      dataIndex: 'documentType',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentType',
      }),
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.documentType,
      sorter: true,
    },
    {
      dataIndex: 'emissionDate',
      title: 'Dt. emissão',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.emissionDate,
      sorter: true,
    },
    {
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.dueDate,
      sorter: true,
    },
    {
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: AccountPayableFieldOption.payDate,
      sorter: true,
    },
    {
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.situation',
      }),
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.situation,
      render: (situation: InstallmentSituation) => (
        <StatusTag
          label={InstallmentSituationType[situation]}
          color={AccountPayableSituationColor[situation]}
        />
      ),
    },
    {
      dataIndex: 'value',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.value',
      }),
      render: (_, record) =>
        `R$ ${quantityFormatter(
          record.installmentValue +
            (record.discountIncrease ?? 0) -
            (record.credit ?? 0),
        )}`,
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.value,
      sorter: true,
    },
  ];

  return (
    <>
      <Popover content="Vincular">
        <ActionIcon onClick={action.open}>
          <Link size={18} />
        </ActionIcon>
      </Popover>

      <Modal
        open={opened}
        onCancel={handleClose}
        title="Vincular contas a pagar cadastrados"
        width="100%"
        footer={false}
        closable={!isLoading}
        maskClosable={!isLoading}
      >
        <p>Selecione uma parcela para vincular a consulta de migração.</p>

        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() => setQueryParams({ order: 'DESC' })}
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <InstallmentsExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
          handleClickAdvancedFilters={advancedFiltersAction.open}
        />

        <InfiniteTable
          dataSource={installments}
          columns={[
            ...columns,
            {
              title: 'Selecionar',
              fixed: 'right',
              align: 'center',
              width: 80,
              render: (_, item) => (
                <Button
                  icon={<Check />}
                  shape="circle"
                  loading={
                    consultPayableBind.isLoading &&
                    consultPayableBind.variables?.mainInstallmentPayableId ===
                      item.id
                  }
                  onClick={() => handleSubmit(item)}
                />
              ),
            },
          ]}
          fetchNextPage={installmentList.fetchNextPage}
          hasNextPage={installmentList.hasNextPage}
          isFetching={installmentList.isFetching}
          loading={installmentList.isLoading}
          rowKey={(item) => item.id}
          scroll={{ x: true }}
        />

        <InstallmentsAdvancedModalFilters
          open={advancedFiltersOpened}
          onCancel={advancedFiltersAction.close}
          handleSetFilter={(values) =>
            setQueryParams((prev) => ({ ...prev, ...values }))
          }
          queryParams={queryParams}
        />
      </Modal>
    </>
  );
}
